"use client"

import { useState, useEffect } from "react"
import { db, ref as dbRef, onValue, auth } from "../Firebase/firebase-config"
import {
  FaFileAlt,
  FaCheckCircle,
  FaChevronDown,
  FaChevronRight,
  FaTruck,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaStar, // Added star icon for new POs
} from "react-icons/fa"
import { useNavigate } from "react-router-dom"
import "./POPage.css"

const POPage = () => {
  const [codes, setCodes] = useState([]) // Grouped by code
  const [selectedPO, setSelectedPO] = useState(null)
  const [expandedCode, setExpandedCode] = useState(null)
  const [loading, setLoading] = useState(true)
  const [newPOIds, setNewPOIds] = useState(new Set()) // Track new PO IDs
  const navigate = useNavigate()

  const hasValue = (value) => {
    return value !== null && value !== undefined && value !== "" && value !== "-"
  }

  const renderDetailItem = (label, value, icon = null, className = "") => {
    if (!hasValue(value)) return null

    return (
      <div className={`popage-detail-item ${className}`}>
        <div className="popage-detail-label">{label}</div>
        <div className="popage-detail-value">
          {icon && icon}
          {value}
        </div>
      </div>
    )
  }

  useEffect(() => {
    const currentUser = auth.currentUser
    console.log("👤 Current user:", currentUser)

    if (!currentUser) {
      console.log("⛔ No logged-in user yet.")
      return
    }

    const userId = currentUser.uid
    const activePORef = dbRef(db, `ActivePO/${userId}`)
    console.log("📡 Subscribing to:", `ActivePO/${userId}`)

    let existingPOIds = new Set()

    const unsubscribe = onValue(activePORef, (snapshot) => {
      console.log("🔥 Firebase snapshot triggered")
      const data = snapshot.val()
      console.log("📦 Data received:", data)

      if (!data) {
        setCodes([])
        setSelectedPO(null)
        setLoading(false)
        return
      }

      const currentPOIds = new Set(Object.keys(data))
      const newPOs = new Set()

      currentPOIds.forEach((poId) => {
        if (!existingPOIds.has(poId)) {
          newPOs.add(poId)
        }
      })

      // Update existing PO IDs for next comparison
      existingPOIds = currentPOIds
      setNewPOIds(newPOs)

      // ✅ Group POs by "code" field (not codeId)
      const grouped = {}
      Object.keys(data).forEach((poId) => {
        const po = { poId, ...data[poId] }
        const codeId = po.code || "Unassigned" // use `code` field

        if (!grouped[codeId]) grouped[codeId] = []
        grouped[codeId].push(po)
      })

      // Convert into array for rendering
      // Convert into array for rendering
let formatted = Object.keys(grouped).map((codeId) => ({
  codeId,
  pos: grouped[codeId],
}));

// Sort so the group containing the newest PO appears on top
formatted.sort((a, b) => {
  const aHasNew = a.pos.some((po) => newPOs.has(po.poId));
  const bHasNew = b.pos.some((po) => newPOs.has(po.poId));

  // If group A has new PO but B doesn't → A goes first
  if (aHasNew && !bHasNew) return -1;
  if (!aHasNew && bHasNew) return 1;

  // Otherwise, sort by latest timestamp inside each group
  const getLatestTime = (posList) => {
    const timestamps = posList.map((po) => new Date(po.timestamp || 0).getTime());
    return Math.max(...timestamps);
  };

  return getLatestTime(b.pos) - getLatestTime(a.pos);
});


      setCodes(formatted)

      formatted.forEach((group) => {
        const hasNewPO = group.pos.some((po) => newPOs.has(po.poId))
        if (hasNewPO) {
          setExpandedCode(group.codeId)
        }
      })

      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  if (loading)
    return (
      <div className="popage-loading-container">
        <div className="popage-loading-spinner"></div>
        <p>Loading POs...</p>
      </div>
    )

  return (
    <div className="popage-page-wrapper">
      <div className="popage-po-container">
        {/* Left: Code & PO List */}
        <div className="popage-po-list">
          <div className="popage-po-list-header">
            <h2>Your Active POs</h2>
            <div className="popage-po-count">{codes.reduce((total, group) => total + group.pos.length, 0)} POs</div>
          </div>
          <div className="popage-po-list-content">
            <div className="popage-code-label">CODE ID's</div>
            {codes.map((group) => (
              <div key={group.codeId} className="popage-code-group">
                <div
                  className="popage-code-header"
                  onClick={() => setExpandedCode(expandedCode === group.codeId ? null : group.codeId)}
                >
                  <div className="popage-code-header-content">
                    {expandedCode === group.codeId ? (
                      <FaChevronDown className="popage-chevron-icon" />
                    ) : (
                      <FaChevronRight className="popage-chevron-icon" />
                    )}
                    <span className="popage-code-name">{group.codeId}</span>
                  </div>
                  <span className="popage-po-badge">{group.pos.length}</span>
                </div>

                {expandedCode === group.codeId && (
                  <div className="popage-po-sublist">
                    {group.pos.map((po) => (
                      <div
                        key={po.poId}
                        onClick={() => setSelectedPO(po)}
                        className={`popage-po-item ${selectedPO?.poId === po.poId ? "popage-selected" : ""} ${newPOIds.has(po.poId) ? "popage-new-po" : ""}`}
                      >
                        <div className="popage-po-item-content">
                          <div className="popage-po-id-container">
                            <span className="popage-po-id">{po.poId}</span>
                            {newPOIds.has(po.poId) && po.status !== "Pending" && (
                              <span className="popage-new-po-badge">
                                <FaStar className="popage-star-icon" />
                                New PO
                              </span>
                            )}

                          </div>
                          <div className="popage-po-meta">
                            <span className="popage-po-brand">{po.brand}</span>
                            <span className="popage-po-quantity">
                              {po.quantity} {po.quantityType}
                            </span>
                          </div>
                        </div>
                        <div className="popage-po-status-icon">
                          {po.status === "Pending" ? (
                            <FaFileAlt className="popage-icon popage-pending" />
                          ) : (
                            <FaCheckCircle className="popage-icon popage-approved" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {codes.length === 0 && (
              <div className="popage-empty-state">
                <FaFileAlt className="popage-empty-icon" />
                <p>No Active POs found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: PO Details */}
        <div className="popage-po-details">
          {selectedPO ? (
            <div className="popage-details-card">
              <div className="popage-details-header">
                <div className="popage-header-content">
                  <h2>PO Details</h2>
                  <span style={{ marginLeft: '0.5rem' }}
                    className={`popage-status-badge ${selectedPO.status === "Pending" ? "popage-pending" : "popage-approved"}`}
                  >
                    {selectedPO.status === "Pending" ? <FaFileAlt /> : <FaCheckCircle />}
                    {selectedPO.status}
                  </span>


                </div>
                <div className="popage-po-id-display">{selectedPO.poId}</div>
              </div>

              <div className="popage-details-content">
                <div className="popage-details-section">
                  <h3>Basic Information</h3>
                  <div className="popage-details-grid">
                    {renderDetailItem("Code", selectedPO.code)}
                    {renderDetailItem("Brand", selectedPO.brand)}
                    {renderDetailItem("Grade", selectedPO.grade)}
                    {renderDetailItem(
                      "Quantity",
                      selectedPO.quantity && selectedPO.quantityType
                        ? `${selectedPO.quantity} ${selectedPO.quantityType}`
                        : null,
                    )}
                    {renderDetailItem("Rate", selectedPO.rate, <FaRupeeSign className="popage-rupee-icon" />)}
                    {renderDetailItem("Company Name", selectedPO.companyName)}
                    {renderDetailItem("Category", selectedPO.category)}
                    {renderDetailItem("TPC Code", selectedPO.tpcCode)}
                    {renderDetailItem("End Use of Cement", selectedPO.endUse)}
                    {renderDetailItem("Packaging", selectedPO.packaging)}
                    {renderDetailItem(
                      "Timestamp",
                      selectedPO.timestamp,
                      <FaCalendarAlt className="popage-calendar-icon" />,
                    )}
                  </div>
                </div>

                {(hasValue(selectedPO.basicPrice) ||
                  hasValue(selectedPO.gstPercentage) ||
                  hasValue(selectedPO.totalPrice)) && (
                    <div className="popage-details-section">
                      <h3>Pricing Information</h3>
                      <div className="popage-details-grid">
                        {renderDetailItem(
                          "Basic Price of Material",
                          selectedPO.basicPrice,
                          <FaRupeeSign className="popage-rupee-icon" />,
                        )}
                        {renderDetailItem(
                          "GST Percentage",
                          selectedPO.gstPercentage ? `${selectedPO.gstPercentage}%` : null,
                        )}
                        {renderDetailItem(
                          "Calculated GST",
                          selectedPO.basicPrice && selectedPO.gstPercentage
                            ? (Number(selectedPO.basicPrice) * Number(selectedPO.gstPercentage)) / 100
                            : null,
                          <FaRupeeSign className="popage-rupee-icon" />,
                        )}
                        {renderDetailItem(
                          "Total Billing Price/Bag",
                          selectedPO.totalPrice,
                          <FaRupeeSign className="popage-rupee-icon" />,
                        )}
                      </div>
                    </div>
                  )}

                {(hasValue(selectedPO.gstNo) || hasValue(selectedPO.panNo) || hasValue(selectedPO.email)) && (
                  <div className="popage-details-section">
                    <h3>Supplier Information</h3>
                    <div className="popage-details-grid">
                      {renderDetailItem("GST No", selectedPO.gstNo)}
                      {renderDetailItem("PAN No", selectedPO.panNo)}
                      {renderDetailItem("Email", selectedPO.email)}
                    </div>
                  </div>
                )}

                {(hasValue(selectedPO.paymentMethod) ||
                  hasValue(selectedPO.paymentAmount) ||
                  hasValue(selectedPO.paymentTerms) ||
                  hasValue(selectedPO.utrNumber)) && (
                    <div className="popage-details-section">
                      <h3>Payment Information</h3>
                      <div className="popage-details-grid">
                        {renderDetailItem(
                          "Payment Method",
                          selectedPO.paymentMethod === "proof"
                            ? "Payment Proof"
                            : selectedPO.paymentMethod === "utr"
                              ? selectedPO.paymentMethod
                              : selectedPO.paymentMethod,
                        )}
                        {renderDetailItem(
                          "Payment Amount",
                          selectedPO.paymentAmount,
                          <FaRupeeSign className="popage-rupee-icon" />,
                        )}
                        {renderDetailItem("Payment Terms", selectedPO.paymentTerms)}
                        {renderDetailItem("UTR Number", selectedPO.utrNumber, null, "popage-full-width")}
                      </div>
                    </div>
                  )}

                {(hasValue(selectedPO.billingAddress) ||
                  hasValue(selectedPO.siteAddress?.address) ||
                  hasValue(selectedPO.contactPerson) ||
                  hasValue(selectedPO.contactMobile) ||
                  hasValue(selectedPO.deliverySchedule)) && (
                    <div className="popage-details-section">
                      <h3>Delivery Information</h3>
                      <div className="popage-details-grid">
                        {renderDetailItem("Billing Address", selectedPO.billingAddress, null, "popage-full-width")}
                        {hasValue(selectedPO.siteAddress) && (
                          <div className="popage-site-address-card">
                            <FaMapMarkerAlt className="popage-location-icon" />
                            <div className="popage-address-content">
                              <div className="popage-detail-label">Site Address</div>
                              <div className="popage-detail-value">
                                {selectedPO.siteAddress.address}
                                {selectedPO.siteAddress.city ? `, ${selectedPO.siteAddress.city}` : ""}
                                {selectedPO.siteAddress.state ? `, ${selectedPO.siteAddress.state}` : ""}
                                {selectedPO.siteAddress.pincode ? ` - ${selectedPO.siteAddress.pincode}` : ""}
                              </div>
                            </div>
                          </div>
                        )}
                        {renderDetailItem("Contact Person", selectedPO.contactPerson)}
                        {renderDetailItem("Contact Mobile", selectedPO.contactMobile)}
                        {renderDetailItem("Delivery Schedule", selectedPO.deliverySchedule)}
                      </div>
                    </div>
                  )}

                {(hasValue(selectedPO.poFileUrl) ||
                  (selectedPO.paymentMethod === "proof" && hasValue(selectedPO.paymentFileUrl))) && (
                    <div className="popage-details-section">
                      <h3>Documents</h3>
                      <div className="popage-documents-grid">
                        {hasValue(selectedPO.poFileUrl) && (
                          <div className="popage-document-item">
                            <div className="popage-detail-label">PO File</div>
                            <a
                              href={selectedPO.poFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="popage-document-link"
                            >
                              <FaFileAlt className="popage-file-icon" />
                              View PO File
                            </a>
                          </div>
                        )}
                        {selectedPO.paymentMethod === "proof" && hasValue(selectedPO.paymentFileUrl) && (
                          <div className="popage-document-item">
                            <div className="popage-detail-label">Payment Proof</div>
                            <a
                              href={selectedPO.paymentFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="popage-document-link"
                            >
                              <FaFileAlt className="popage-file-icon" />
                              View Payment File
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              <div className="popage-details-footer">
                <div className="popage-status-section">
                  {/* <div className="popage-detail-label">Status</div>
                  <span
                    className={`popage-status-badge ${selectedPO.status === "Pending" ? "popage-pending" : "popage-approved"}`}
                  >
                    {selectedPO.status === "Pending" ? <FaFileAlt /> : <FaCheckCircle />}
                    {selectedPO.status}
                  </span> */}
                </div>

                <button
                  className="popage-track-btn"
                  onClick={() => navigate(`/dashboard/order-status?poId=${selectedPO.poId}`)}
                >
                  <FaTruck className="popage-truck-icon" />
                  Track Order
                </button>
              </div>
            </div>
          ) : (
            <div className="popage-empty-selection">
              <FaFileAlt className="popage-empty-icon" />
              <h3>Select a PO to view details</h3>
              <p>Choose a Code → PO from the left sidebar to view detailed information</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default POPage
